import React, { useEffect, useMemo, useState } from 'react';
import { Currency, DemeterPaymentType, DemeterRegion, DemeterUserType } from '../../../../Generated/Raven-Demeter';
import { useApplicationSelector } from '../../../../Redux/ReduxStore';
import { selectUserType } from '../../../../Redux/Slices/UserSlice';
import CheckboxInput from '../../../Components/Form/Inputs/CheckboxInput';
import SelectInput from '../../../Components/Form/Inputs/SelectInput';
import TextInput from '../../../Components/Form/Inputs/TextInput';
import LoadingSpinner from '../../../Components/LoadingSpinner/LoadingSpinner';
import RegexValidators from '../../../Core/Validation/RegexValidators';
import useLanguage from '../../../Services/Language/useLanguageHook';
import styles from './CompaniesPage.module.scss';

export type UpsertCompanyBillingRequest = {
    billingContactName: string;
    billingCompanyName: string;
    billingAddress1: string;
    billingAddress2: string;
    billingCity: string;
    billingState: string;
    billingCountry: string;
    billingPostalCode: string;
    billingPhone: string;
    billingFax: string;
    billingEmail: string;
    billingVatNumber: string | null;
    billingSameAsCompanyInformation: boolean;
    billingCurrency?: Currency | string;
    billingRegion?: DemeterRegion | string;
    paymentType?: DemeterPaymentType | null;
    futuresAccountTransferConsent?: boolean;
    futuresAccountNumber?: string;
    futuresAccountCustomerName?: string;
    futuresAccountTitle?: string;
};

interface CompanyBillingFormProps {
    userCompanyName: string | null;
    companyBilling: UpsertCompanyBillingRequest;
    handleCompanyBillingIsValid: (isValid: boolean) => void;
    handleCompanyBillingChange: (companyDetails: UpsertCompanyBillingRequest) => void;
}

const CompanyBillingForm = (props: CompanyBillingFormProps) => {
    const [translations] = useLanguage();
    const currentUserType = useApplicationSelector(selectUserType);
    const [companyVatNotRequired, setCompanyVatNotRequired] = useState<boolean>(false);

    const [companyBilling, setCompanyBilling] = useState<UpsertCompanyBillingRequest>();

    const isBusinessOwnerOrAdministrator = useMemo(
        () => currentUserType === DemeterUserType.BusinessOwner || currentUserType === DemeterUserType.Administrator,
        [currentUserType],
    );

    const paymentTypeOptions = useMemo(
        () => [
            {
                value: undefined,
                label: '',
            },
            {
                value: DemeterPaymentType.WireTransfer,
                label: translations.paymentType.WireTransfer,
            },
            {
                value: DemeterPaymentType.DeductionFromFuturesAccount,
                label: translations.paymentType.DeductionFromFuturesAccount,
            },
        ],
        [translations],
    );

    const currencyOptions = useMemo(
        () => [
            {
                value: undefined,
                label: '',
            },
            {
                value: Currency.Usd,
                label: translations.currency.USD,
            },
            {
                value: Currency.Eur,
                label: translations.currency.EUR,
            },
        ],
        [translations],
    );

    const regionOptions = useMemo(
        () => [
            {
                value: undefined,
                label: '',
            },
            {
                value: DemeterRegion.America,
                label: translations.region.America,
            },
            {
                value: DemeterRegion.EuropeMiddleEastAndAfrica,
                label: translations.region.EuropeMiddleEastAndAfrica,
            },
            {
                value: DemeterRegion.AsiaPacific,
                label: translations.region.AsiaPacific,
            },
        ],
        [translations],
    );

    useEffect(() => {
        if (
            (isBusinessOwnerOrAdministrator && companyBilling) ||
            (companyBilling &&
                ((!companyBilling.billingSameAsCompanyInformation &&
                    !!companyBilling.billingContactName &&
                    !!companyBilling.billingCompanyName &&
                    !!companyBilling.billingAddress1 &&
                    !!companyBilling.billingCity &&
                    !!companyBilling.billingState &&
                    !!companyBilling.billingCountry &&
                    !!companyBilling.billingPostalCode &&
                    !!companyBilling.billingPhone) ||
                    companyBilling.billingSameAsCompanyInformation) &&
                !!companyBilling.billingEmail &&
                (companyVatNotRequired ||
                    (!!companyBilling.billingVatNumber && companyBilling.billingVatNumber !== translations.text.notAvailableAbbreviation)) &&
                (props.companyBilling.paymentType === undefined || !!companyBilling.paymentType) &&
                (companyBilling.paymentType !== DemeterPaymentType.DeductionFromFuturesAccount ||
                    ((props.companyBilling.futuresAccountTransferConsent === undefined || !!companyBilling.futuresAccountTransferConsent) &&
                        (props.companyBilling.futuresAccountNumber === undefined || !!companyBilling.futuresAccountNumber) &&
                        (props.companyBilling.futuresAccountCustomerName === undefined || !!companyBilling.futuresAccountCustomerName) &&
                        (props.companyBilling.futuresAccountTitle === undefined || !!companyBilling.futuresAccountTitle))))
        ) {
            props.handleCompanyBillingIsValid(true);
            props.handleCompanyBillingChange(companyBilling);
            return;
        }

        props.handleCompanyBillingIsValid(false);
    }, [companyBilling, companyVatNotRequired]);

    useEffect(() => {
        if (companyBilling) {
            return;
        }

        setCompanyBilling(props.companyBilling);
        setCompanyVatNotRequired(!!props.companyBilling?.billingVatNumber);
    }, [props.companyBilling]);

    return !companyBilling ? (
        <LoadingSpinner />
    ) : (
        <div>
            <div className={styles.add_edit_company_checkbox_input}>
                <CheckboxInput
                    title={translations.companies.fields.billingSameAsCompanyInformation}
                    isChecked={companyBilling?.billingSameAsCompanyInformation ?? false}
                    handleIsChecked={(value) => setCompanyBilling({ ...companyBilling, billingSameAsCompanyInformation: value })}
                    track={{
                        eventCategory: 'Company',
                        eventAction: 'BillingSameAsCompanyInformationChanged',
                        eventValue: companyBilling?.billingSameAsCompanyInformation,
                    }}
                />
            </div>
            {(!companyBilling.billingSameAsCompanyInformation || !props.userCompanyName) && (
                <div className={styles.add_edit_company_input}>
                    <TextInput
                        title={translations.companies.fields.billingCompanyName}
                        placeholder={translations.companies.fields.billingCompanyName}
                        required={!isBusinessOwnerOrAdministrator}
                        denoteRequired={!isBusinessOwnerOrAdministrator}
                        value={companyBilling?.billingCompanyName}
                        handleTextChange={(value) => setCompanyBilling({ ...companyBilling, billingCompanyName: value })}
                        validation={RegexValidators.AlphaNumericMinimumLength2}
                        errorMessage={translations.companies.errors.requiredField}
                        track={{
                            eventCategory: 'Company',
                            eventAction: 'BillingCompanyNameChanged',
                            eventValue: companyBilling?.billingCompanyName,
                        }}
                    />
                </div>
            )}
            {companyBilling.billingSameAsCompanyInformation !== true && (
                <div>
                    <div className={styles.add_edit_company_input}>
                        <TextInput
                            title={translations.companies.fields.billingContactName}
                            placeholder={translations.companies.fields.billingContactName}
                            required={!isBusinessOwnerOrAdministrator}
                            denoteRequired={!isBusinessOwnerOrAdministrator}
                            value={companyBilling?.billingContactName}
                            handleTextChange={(value) => setCompanyBilling({ ...companyBilling, billingContactName: value })}
                            validation={RegexValidators.AlphaNumericMinimumLength2}
                            errorMessage={translations.companies.errors.requiredField}
                            track={{
                                eventCategory: 'Company',
                                eventAction: 'BillingContactNameChanged',
                                eventValue: companyBilling?.billingContactName,
                            }}
                        />
                    </div>
                    <div className={styles.add_edit_company_input}>
                        <TextInput
                            title={translations.companies.fields.billingAddress1}
                            placeholder={translations.companies.fields.billingAddress1}
                            required={!isBusinessOwnerOrAdministrator}
                            denoteRequired={!isBusinessOwnerOrAdministrator}
                            value={companyBilling?.billingAddress1}
                            handleTextChange={(value) => setCompanyBilling({ ...companyBilling, billingAddress1: value })}
                            validation={RegexValidators.AlphaNumericMinimumLength2}
                            errorMessage={translations.companies.errors.requiredField}
                            track={{
                                eventCategory: 'Company',
                                eventAction: 'BillingAddress1Changed',
                                eventValue: companyBilling?.billingAddress1,
                            }}
                        />
                    </div>
                    <div className={styles.add_edit_company_input}>
                        <TextInput
                            title={translations.companies.fields.billingAddress2}
                            placeholder={translations.companies.fields.billingAddress2}
                            value={companyBilling?.billingAddress2}
                            handleTextChange={(value) => setCompanyBilling({ ...companyBilling, billingAddress2: value })}
                            validation={RegexValidators.AlphaNumericMinimumLength2}
                            errorMessage={translations.companies.errors.requiredField}
                            track={{
                                eventCategory: 'Company',
                                eventAction: 'BillingAddress2Changed',
                                eventValue: companyBilling?.billingAddress2,
                            }}
                        />
                    </div>
                    <div className={styles.add_edit_company_input}>
                        <TextInput
                            title={translations.companies.fields.billingCity}
                            placeholder={translations.companies.fields.billingCity}
                            required={!isBusinessOwnerOrAdministrator}
                            denoteRequired={!isBusinessOwnerOrAdministrator}
                            value={companyBilling?.billingCity}
                            handleTextChange={(value) => setCompanyBilling({ ...companyBilling, billingCity: value })}
                            validation={RegexValidators.AlphaNumericMinimumLength2}
                            errorMessage={translations.companies.errors.requiredField}
                            track={{
                                eventCategory: 'Company',
                                eventAction: 'BillingCityChanged',
                                eventValue: companyBilling?.billingCity,
                            }}
                        />
                    </div>
                    <div className={styles.add_edit_company_input}>
                        <TextInput
                            title={translations.companies.fields.billingState}
                            placeholder={translations.companies.fields.billingState}
                            required={!isBusinessOwnerOrAdministrator}
                            denoteRequired={!isBusinessOwnerOrAdministrator}
                            value={companyBilling?.billingState}
                            handleTextChange={(value) => setCompanyBilling({ ...companyBilling, billingState: value })}
                            validation={RegexValidators.AlphaNumericMinimumLength2}
                            errorMessage={translations.companies.errors.requiredField}
                            track={{
                                eventCategory: 'Company',
                                eventAction: 'BillingStateChanged',
                                eventValue: companyBilling?.billingState,
                            }}
                        />
                    </div>
                    <div className={styles.add_edit_company_input}>
                        <TextInput
                            title={translations.companies.fields.billingCountry}
                            placeholder={translations.companies.fields.billingCountry}
                            required={!isBusinessOwnerOrAdministrator}
                            denoteRequired={!isBusinessOwnerOrAdministrator}
                            value={companyBilling?.billingCountry}
                            handleTextChange={(value) => setCompanyBilling({ ...companyBilling, billingCountry: value })}
                            validation={RegexValidators.AlphaNumericMinimumLength2}
                            errorMessage={translations.companies.errors.requiredField}
                            track={{
                                eventCategory: 'Company',
                                eventAction: 'BillingCountryChanged',
                                eventValue: companyBilling?.billingCountry,
                            }}
                        />
                    </div>
                    <div className={styles.add_edit_company_input}>
                        <TextInput
                            title={translations.companies.fields.billingPostalCode}
                            placeholder={translations.companies.fields.billingPostalCode}
                            required={!isBusinessOwnerOrAdministrator}
                            denoteRequired={!isBusinessOwnerOrAdministrator}
                            value={companyBilling?.billingPostalCode}
                            handleTextChange={(value) => setCompanyBilling({ ...companyBilling, billingPostalCode: value })}
                            validation={RegexValidators.AlphaNumericMinimumLength2}
                            errorMessage={translations.companies.errors.requiredField}
                            track={{
                                eventCategory: 'Company',
                                eventAction: 'BillingPostalCodeChanged',
                                eventValue: companyBilling?.billingPostalCode,
                            }}
                        />
                    </div>
                    <div className={styles.add_edit_company_input}>
                        <TextInput
                            title={translations.companies.fields.billingPhone}
                            placeholder={translations.companies.fields.billingPhone}
                            required={!isBusinessOwnerOrAdministrator}
                            denoteRequired={!isBusinessOwnerOrAdministrator}
                            value={companyBilling?.billingPhone}
                            handleTextChange={(value) => setCompanyBilling({ ...companyBilling, billingPhone: value })}
                            validation={RegexValidators.Phone}
                            errorMessage={translations.companies.errors.requiredField}
                            track={{
                                eventCategory: 'Company',
                                eventAction: 'BillingPhoneChanged',
                                eventValue: companyBilling?.billingPhone,
                            }}
                        />
                    </div>
                </div>
            )}

            <div className={styles.add_edit_company_input}>
                <TextInput
                    title={translations.companies.fields.billingVatNumber}
                    placeholder={!companyVatNotRequired ? translations.companies.fields.billingVatNumber : translations.text.notAvailableAbbreviation}
                    required={!companyVatNotRequired && !isBusinessOwnerOrAdministrator}
                    denoteRequired={!companyVatNotRequired && !isBusinessOwnerOrAdministrator}
                    disabled={companyVatNotRequired}
                    value={!companyVatNotRequired ? companyBilling?.billingVatNumber : translations.text.notAvailableAbbreviation}
                    // eslint-disable-next-line no-confusing-arrow
                    handleTextChange={(value) =>
                        !companyVatNotRequired
                            ? setCompanyBilling({ ...companyBilling, billingVatNumber: value })
                            : setCompanyBilling({
                                  ...companyBilling,
                                  billingVatNumber: null,
                              })
                    }
                    validation={RegexValidators.AlphaNumericWithDashOrDot}
                    errorMessage={translations.companies.errors.requiredField}
                    track={{
                        eventCategory: 'Company',
                        eventAction: 'BillingVatNumberChanged',
                        eventValue: companyBilling?.billingVatNumber,
                    }}
                />
            </div>
            <div className={styles.add_edit_company_checkbox_input}>
                <CheckboxInput
                    title={translations.companies.text.vatNotApplicable}
                    isChecked={companyVatNotRequired}
                    handleIsChecked={(value) => setCompanyVatNotRequired(value)}
                    track={{ eventCategory: 'Company', eventAction: 'VatNotRequiredChanged', eventValue: companyVatNotRequired }}
                />
            </div>
            {props.companyBilling.paymentType !== undefined && (
                <div className={styles.add_edit_company_input}>
                    <SelectInput
                        title={translations.companies.fields.paymentType}
                        value={companyBilling?.paymentType}
                        options={paymentTypeOptions}
                        handleOptionChange={(value) => setCompanyBilling({ ...companyBilling, paymentType: value })}
                        track={{ eventCategory: 'Company', eventAction: 'PaymentTypeChanged', eventValue: companyBilling?.paymentType }}
                    />
                </div>
            )}
            {props.companyBilling.paymentType !== undefined && companyBilling?.paymentType === DemeterPaymentType.DeductionFromFuturesAccount && (
                <>
                    <div className={styles.add_edit_company_input}>
                        <TextInput
                            title={translations.companies.fields.futuresAccountNumber}
                            placeholder={translations.companies.fields.futuresAccountNumber}
                            required={!isBusinessOwnerOrAdministrator}
                            denoteRequired={!isBusinessOwnerOrAdministrator}
                            value={companyBilling?.futuresAccountNumber}
                            handleTextChange={(value) => setCompanyBilling({ ...companyBilling, futuresAccountNumber: value })}
                            validation={RegexValidators.AlphaNumericMinimumLength2}
                            errorMessage={translations.companies.errors.requiredField}
                            track={{ eventCategory: 'Company', eventAction: 'FuturesAccountNumberChanged', eventValue: companyBilling?.futuresAccountNumber }}
                        />
                    </div>
                    <div className={styles.add_edit_company_input}>
                        <TextInput
                            title={translations.companies.fields.futuresAccountCustomerName}
                            placeholder={translations.companies.fields.futuresAccountCustomerName}
                            required={!isBusinessOwnerOrAdministrator}
                            denoteRequired={!isBusinessOwnerOrAdministrator}
                            value={companyBilling?.futuresAccountCustomerName}
                            handleTextChange={(value) => setCompanyBilling({ ...companyBilling, futuresAccountCustomerName: value })}
                            validation={RegexValidators.AlphaNumericMinimumLength2}
                            errorMessage={translations.companies.errors.requiredField}
                            track={{
                                eventCategory: 'Company',
                                eventAction: 'FuturesAccountCustomerNameChanged',
                                eventValue: companyBilling?.futuresAccountCustomerName,
                            }}
                        />
                    </div>
                    <div className={styles.add_edit_company_input}>
                        <TextInput
                            title={translations.companies.fields.futuresAccountTitle}
                            placeholder={translations.companies.fields.futuresAccountTitle}
                            required={!isBusinessOwnerOrAdministrator}
                            denoteRequired={!isBusinessOwnerOrAdministrator}
                            value={companyBilling?.futuresAccountTitle}
                            handleTextChange={(value) => setCompanyBilling({ ...companyBilling, futuresAccountTitle: value })}
                            validation={RegexValidators.AlphaNumericMinimumLength2}
                            errorMessage={translations.companies.errors.requiredField}
                            track={{
                                eventCategory: 'Company',
                                eventAction: 'FuturesAccountTitleChanged',
                                eventValue: companyBilling?.futuresAccountTitle,
                            }}
                        />
                    </div>

                    <div className={styles.add_edit_company_input}>
                        <CheckboxInput
                            title={translations.companies.fields.futuresAccountTransferConsent}
                            isChecked={companyBilling?.futuresAccountTransferConsent ?? false}
                            handleIsChecked={(value) => setCompanyBilling({ ...companyBilling, futuresAccountTransferConsent: value })}
                            track={{
                                eventCategory: 'Company',
                                eventAction: 'FuturesAccountTransferConsentChanged',
                                eventValue: companyBilling?.futuresAccountTransferConsent,
                            }}
                        />
                    </div>
                </>
            )}
            <div className={styles.add_edit_company_input}>
                <TextInput
                    title={translations.companies.fields.billingFax}
                    placeholder={translations.companies.fields.billingFax}
                    value={companyBilling?.billingFax}
                    handleTextChange={(value) => setCompanyBilling({ ...companyBilling, billingFax: value })}
                    track={{ eventCategory: 'Company', eventAction: 'BillingFaxChanged', eventValue: companyBilling?.billingFax }}
                />
            </div>
            <div className={styles.add_edit_company_input}>
                <TextInput
                    title={translations.companies.fields.billingEmail}
                    placeholder={translations.companies.fields.billingEmail}
                    required={!isBusinessOwnerOrAdministrator}
                    denoteRequired={!isBusinessOwnerOrAdministrator}
                    value={companyBilling?.billingEmail}
                    handleTextChange={(value) => setCompanyBilling({ ...companyBilling, billingEmail: value })}
                    validation={RegexValidators.Email}
                    errorMessage={translations.companies.errors.requiredField}
                    track={{
                        eventCategory: 'Company',
                        eventAction: 'BillingEmailChanged',
                        eventValue: companyBilling?.billingEmail,
                    }}
                />
            </div>
            {props.companyBilling.billingCurrency !== undefined && (
                <div className={styles.add_edit_company_input}>
                    <SelectInput
                        title={translations.companies.fields.billingCurrency}
                        value={companyBilling?.billingCurrency}
                        options={currencyOptions}
                        handleOptionChange={(value) => setCompanyBilling({ ...companyBilling, billingCurrency: value })}
                        track={{ eventCategory: 'Company', eventAction: 'BillingCurrencyChanged', eventValue: companyBilling?.billingCurrency }}
                    />
                </div>
            )}
            {props.companyBilling.billingRegion !== undefined && (
                <div className={styles.add_edit_company_input}>
                    <SelectInput
                        title={translations.companies.fields.billingRegion}
                        value={companyBilling?.billingRegion}
                        options={regionOptions}
                        handleOptionChange={(value) => setCompanyBilling({ ...companyBilling, billingRegion: value })}
                        track={{ eventCategory: 'Company', eventAction: 'BillingRegionChanged', eventValue: companyBilling?.billingRegion }}
                    />
                </div>
            )}
            {!isBusinessOwnerOrAdministrator && (
                <div className={styles.add_edit_company_input}>
                    <em>* {translations.companies.text.indicatesRequiredField}</em>
                </div>
            )}
        </div>
    );
};
export default CompanyBillingForm;
